'use client'

import { useEffect } from 'react'
import { isAuthenticatedPlayer } from 'src/types/domain/Player'
import type { Balance } from 'src/types/domain/Player'
import { usePlayerStore } from 'src/store/player'
import { useErrorStore, type AppError } from 'src/store/error'
import { PostMessageBridge as Bridge } from 'src/services/postMessage/PostMessageBridge'
import { registerInboundHandlers } from 'src/services/postMessage/handlers'
import {
  emitBalanceChanged,
  emitGameError,
  emitSessionExpired
} from 'src/services/postMessage/emitters'

/**
 * Initializes the PostMessage bridge and subscribes to store changes
 * for automatic outbound event emission.
 *
 * Rendered once in layout.tsx alongside ListenersWrapper.
 */
export function PostMessageBridgeComponent() {
  // Initialize bridge and register handlers
  useEffect(() => {
    const bridge = Bridge.getInstance()
    bridge.init()
    registerInboundHandlers()
    bridge.setReady()

    return () => {
      bridge.destroy()
    }
  }, [])

  // Subscribe to balance changes and session expiry
  useEffect(() => {
    let prevBalance: Balance | null = null
    let prevPlayerType: string | undefined

    const unsubscribe = usePlayerStore.subscribe((state) => {
      const isAuthenticated = isAuthenticatedPlayer(state.currentPlayer)
      const currentType = state.currentPlayer.type

      // Detect session expiry
      if (prevPlayerType === 'AuthenticatedPlayer' && currentType === 'AnonymousPlayer') {
        emitSessionExpired()
      }
      prevPlayerType = currentType

      // Detect balance change. Gate on balanceLoaded so we never emit the
      // non-blocking-launch placeholder (amount 0) to the operator lobby — that
      // would flash a $0 balance for 2-6s on every launch. The first REAL balance
      // still emits: balanceChanged sets balance + balanceLoaded atomically, so the
      // subscriber sees balanceLoaded=true with the real amount and prevBalance=null.
      if (isAuthenticated && isAuthenticatedPlayer(state.currentPlayer) && state.currentPlayer.balanceLoaded) {
        const balance = state.currentPlayer.balance
        if (
          prevBalance === null ||
          prevBalance.amount !== balance.amount ||
          prevBalance.currency !== balance.currency
        ) {
          prevBalance = balance
          emitBalanceChanged(balance)
        }
      } else {
        prevBalance = null
      }
    })

    return unsubscribe
  }, [])

  // Subscribe to fatal errors
  useEffect(() => {
    let prevError: AppError | undefined

    const unsubscribe = useErrorStore.subscribe((state) => {
      if (state.fatalError && state.fatalError !== prevError) {
        prevError = state.fatalError
        emitGameError({ code: state.fatalError.type, message: state.fatalError.reason })
      }
    })

    return unsubscribe
  }, [])

  return null
}
